/**
 * ============================================
 * SESSION VALIDATOR - Validación Centralizada
 * ============================================
 * Verifica que la sesión del usuario sea válida
 * tanto en localStorage como en el servidor
 */

const SessionValidator = {
    _debugLog(...args) {
        // Solo mostrar logs si session_debug está activo
        if (localStorage.getItem('session_debug') === '1') {
            console.log('[SessionValidator]', ...args);
        }
    },
    /**
     * Asegura claves mínimas de DEMO cuando demo está activo
     */
    ensureDemoIdentityKeys() {
        try {
            const demoToken = 'demo_token_local';
            const demoUserId = 'demo_user';
            const demoEmail = 'demo@tripulacionfx.com';

            if (!sessionStorage.getItem('tfx_authToken')) sessionStorage.setItem('tfx_authToken', demoToken);
            if (!sessionStorage.getItem('tfx_token')) sessionStorage.setItem('tfx_token', demoToken);
            if (!sessionStorage.getItem('tfx_userId')) sessionStorage.setItem('tfx_userId', demoUserId);
            if (!sessionStorage.getItem('userToken')) sessionStorage.setItem('userToken', demoToken);
            if (!sessionStorage.getItem('currentUserId')) sessionStorage.setItem('currentUserId', demoUserId);
            if (!sessionStorage.getItem('userEmail')) sessionStorage.setItem('userEmail', demoEmail);

            if (!localStorage.getItem('tfx_authToken')) localStorage.setItem('tfx_authToken', demoToken);
            if (!localStorage.getItem('tfx_token')) localStorage.setItem('tfx_token', demoToken);
            if (!localStorage.getItem('tfx_userId')) localStorage.setItem('tfx_userId', demoUserId);
            if (!localStorage.getItem('userToken')) localStorage.setItem('userToken', demoToken);
            if (!localStorage.getItem('currentUserId')) localStorage.setItem('currentUserId', demoUserId);
            if (!localStorage.getItem('userEmail')) localStorage.setItem('userEmail', demoEmail);
            if (!localStorage.getItem('userPlan')) localStorage.setItem('userPlan', 'unlimited');
        } catch (e) {
            // ignore
        }
    },
    /**
     * Detecta si el estado actual corresponde a DEMO
     */
    isDemoSession() {
        try {
            const demoToken = 'demo_token_local';
            const demoUserId = 'demo_user';

            let demoParamActive = false;
            try {
                const params = new URLSearchParams(window.location.search || '');
                const demoParam = params.get('demo');
                demoParamActive = demoParam === '1' || demoParam === 'true' || demoParam === 'yes';
            } catch (e) {}

            const token = sessionStorage.getItem('tfx_authToken') || sessionStorage.getItem('tfx_token') ||
                          sessionStorage.getItem('userToken') || localStorage.getItem('tfx_authToken') ||
                          localStorage.getItem('tfx_token') || localStorage.getItem('userToken');
            const userId = sessionStorage.getItem('tfx_userId') || sessionStorage.getItem('currentUserId') ||
                          localStorage.getItem('tfx_userId') || localStorage.getItem('currentUserId');

            const result = demoParamActive || token === demoToken || userId === demoUserId || !!localStorage.getItem('demo_db_v1') || localStorage.getItem('demo_mode') === 'true';
            this._debugLog('isDemoSession', { demoParamActive, token, userId, hasDemoDb: !!localStorage.getItem('demo_db_v1'), demoMode: localStorage.getItem('demo_mode'), result });
            return result;
        } catch (e) {
            this._debugLog('isDemoSession error', e);
            return false;
        }
    },

    /**
     * Normaliza claves de sesión entre formatos nuevo/antiguo
     */
    normalizeSessionKeys() {
        try {
            const tfxToken = sessionStorage.getItem('tfx_token') || localStorage.getItem('tfx_token');
            const tfxAuth = sessionStorage.getItem('tfx_authToken') || localStorage.getItem('tfx_authToken');
            if (!sessionStorage.getItem('tfx_token') && tfxAuth) {
                sessionStorage.setItem('tfx_token', tfxAuth);
            }
            if (!sessionStorage.getItem('tfx_authToken') && tfxToken) {
                sessionStorage.setItem('tfx_authToken', tfxToken);
            }

            const oldToken = sessionStorage.getItem('userToken') || sessionStorage.getItem('authToken') ||
                             localStorage.getItem('userToken') || localStorage.getItem('authToken');
            if (oldToken && !sessionStorage.getItem('tfx_token')) {
                sessionStorage.setItem('tfx_token', oldToken);
                sessionStorage.setItem('tfx_authToken', oldToken);
            }

            const oldUserId = sessionStorage.getItem('currentUserId') || localStorage.getItem('currentUserId');
            if (oldUserId && !sessionStorage.getItem('tfx_userId')) {
                sessionStorage.setItem('tfx_userId', oldUserId);
            }
        } catch (e) {
            // ignore
        }
    },
    /**
     * Verifica si la sesión es válida en el cliente
     */
    isClientSessionValid() {
        this._debugLog('isClientSessionValid START');
        this.normalizeSessionKeys();

        const isDemo = this.isDemoSession();
        this._debugLog('isClientSessionValid isDemo:', isDemo);
        if (isDemo) {
            this._debugLog('isClientSessionValid: DEMO detected, returning true');
            try { localStorage.setItem('demo_mode', 'true'); } catch (e) {}
            try { localStorage.removeItem('_sessionExpired'); } catch (e) {}
            this.ensureDemoIdentityKeys();
            try {
                const params = new URLSearchParams(window.location.search || '');
                if (params.has('demo')) {
                    params.delete('demo');
                    const newQuery = params.toString();
                    const newUrl = window.location.pathname + (newQuery ? `?${newQuery}` : '');
                    window.history.replaceState({}, '', newUrl);
                }
            } catch (e) {}
            return true;
        }

        try {
            if (localStorage.getItem('_sessionExpired') === 'true') {
                this._debugLog('isClientSessionValid: _sessionExpired=true, returning false');
                return false;
            }
        } catch (e) {}

        try {
            const legacyToken = localStorage.getItem('userToken') || localStorage.getItem('authToken');
            const legacyUserId = localStorage.getItem('currentUserId');
            const legacyEmail = localStorage.getItem('userEmail');
            if (legacyToken && !sessionStorage.getItem('userToken')) {
                sessionStorage.setItem('userToken', legacyToken);
                sessionStorage.setItem('authToken', legacyToken);
                localStorage.removeItem('userToken');
                localStorage.removeItem('authToken');
            }
            if (legacyUserId && !sessionStorage.getItem('currentUserId')) {
                sessionStorage.setItem('currentUserId', legacyUserId);
                localStorage.removeItem('currentUserId');
            }
            if (legacyEmail && !sessionStorage.getItem('userEmail')) {
                sessionStorage.setItem('userEmail', legacyEmail);
                localStorage.removeItem('userEmail');
            }
        } catch (e) {
            // ignore storage errors
        }

        // Verificar nuevas keys
        const newToken = sessionStorage.getItem('tfx_authToken') || sessionStorage.getItem('tfx_token') ||
                 localStorage.getItem('tfx_authToken') || localStorage.getItem('tfx_token');
        const newUserId = sessionStorage.getItem('tfx_userId') || localStorage.getItem('tfx_userId');
        
        // Verificar keys antiguas (retrocompatibilidad)
        const oldToken = sessionStorage.getItem('userToken') || sessionStorage.getItem('authToken') ||
                 localStorage.getItem('userToken') || localStorage.getItem('authToken');
        const oldUserId = sessionStorage.getItem('currentUserId') || localStorage.getItem('currentUserId');
        
        const hasSession = (newToken && newUserId) || (oldToken && oldUserId);

        if (!hasSession) {
            try {
                const strayKeys = [
                    'tfx_userId', 'tfx_email', 'tfx_username', 'tfx_plan',
                    'currentUserId', 'userEmail', 'userName', 'userPlan'
                ];
                const hasStrayIdentity = strayKeys.some(key =>
                    sessionStorage.getItem(key) || localStorage.getItem(key)
                );
                if (hasStrayIdentity) {
                    this.clearSession();
                }
            } catch (e) {
                // ignore storage errors
            }
        }

        this._debugLog('isClientSessionValid', { newToken, newUserId, oldToken, oldUserId, hasSession });
        return !!hasSession;
    },

    /**
     * Valida sesión con el servidor
     */
    async validateWithServer() {
        try {
            if (this.isDemoSession()) return true;

            const token = sessionStorage.getItem('tfx_authToken') || 
                         sessionStorage.getItem('tfx_token') || 
                         sessionStorage.getItem('userToken') ||
                         localStorage.getItem('tfx_authToken') || 
                         localStorage.getItem('tfx_token') || 
                         localStorage.getItem('userToken');

            if (token === 'demo_token_local') return true;
            
            if (!token) {
                this._debugLog('validateWithServer: no token');
                return false;
            }

            const baseUrl = (typeof window !== 'undefined' && window.WORKER_URL)
                ? window.WORKER_URL
                : 'https://trading-journal-preprod.monkeydfx-trader.workers.dev';
            const response = await fetch(`${baseUrl}/api/validate-token`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                // Token no válido o expirado
                this._debugLog('validateWithServer: invalid response', response.status);
                this.clearSession();
                return false;
            }

            const data = await response.json();
            const isValid = data.valid === true;
            this._debugLog('validateWithServer result', isValid, data);
            return isValid;
        } catch (error) {
            this._debugLog('validateWithServer error', error);
            return false;
        }
    },

    /**
     * Limpia la sesión completamente
     */
    clearSession() {
        this._debugLog('clearSession');
        const wasDemo = this.isDemoSession();
        // Limpiar nuevas keys
        const newKeys = ['tfx_token', 'tfx_authToken', 'tfx_email', 'tfx_userId', 'tfx_username', 
                'tfx_plan', 'tfx_userType', 'tfx_expiresAt', 'tfx_sessionId', 'tfx_paypalSubId'];
        newKeys.forEach(key => {
            localStorage.removeItem(key);
            sessionStorage.removeItem(key);
        });

        // Limpiar keys antiguas
        const oldKeys = ['userToken', 'authToken', 'currentUserId', 'userEmail', 'userName', 
                'userPlan', 'userType', 'expiresAt', 'stripeCustomerId', 'paypalSubscriptionId', 'userCreatedAt'];
        const miscKeys = ['username'];
        oldKeys.forEach(key => {
            localStorage.removeItem(key);
            sessionStorage.removeItem(key);
        });
        miscKeys.forEach(key => {
            localStorage.removeItem(key);
            sessionStorage.removeItem(key);
        });

        // Limpiar sessionStorage
        sessionStorage.clear();
        
        // Marcar que expiró (pero NO si fue logout voluntario)
        // FIX: No setear _sessionExpired si fue logout voluntario, evita mostrar mensajes falsos
        const isVoluntaryLogout = localStorage.getItem('_logoutVoluntario') === 'true';
        if (!wasDemo && !isVoluntaryLogout) {
            localStorage.setItem('_sessionExpired', 'true');
        }
    },

    /**
     * Valida sesión y redirige si no es válida
     * @param {string} redirectUrl - URL a redirigir si no es válida
     * @returns {Promise<boolean>} true si sesión válida, false si no
     */
    async validateOrRedirect(redirectUrl = '/') {
        this._debugLog('validateOrRedirect START');
        // Primero validar en cliente
        const clientValid = this.isClientSessionValid();
        this._debugLog('validateOrRedirect clientValid:', clientValid);
        if (!clientValid) {
            this.clearSession();
            try { localStorage.setItem('_sessionExpired', 'true'); } catch (e) {}
            this._debugLog('validateOrRedirect: client invalid -> redirect', redirectUrl);
            window.location.href = redirectUrl;
            return false;
        }

        // Luego validar con servidor
        this._debugLog('validateOrRedirect: validating with server...');
        const isValid = await this.validateWithServer();
        this._debugLog('validateOrRedirect serverValid:', isValid);
        if (!isValid) {
            this.clearSession();
            try { localStorage.setItem('_sessionExpired', 'true'); } catch (e) {}
            this._debugLog('validateOrRedirect: server invalid -> redirect', redirectUrl);
            window.location.href = redirectUrl;
            return false;
        }

        this._debugLog('validateOrRedirect: SUCCESS');
        return true;
    },

    /**
     * Obtiene el token actual (compatible con ambos formatos)
     */
    getToken() {
        return sessionStorage.getItem('tfx_authToken') || 
               sessionStorage.getItem('tfx_token') || 
               sessionStorage.getItem('userToken') ||
               localStorage.getItem('tfx_authToken') || 
               localStorage.getItem('tfx_token') || 
               localStorage.getItem('userToken');
    },

    /**
     * Obtiene el user ID actual (compatible con ambos formatos)
     */
    getUserId() {
        return sessionStorage.getItem('tfx_userId') || 
               sessionStorage.getItem('currentUserId') ||
               localStorage.getItem('tfx_userId') || 
               localStorage.getItem('currentUserId');
    }
};

try {
    window.SessionValidator = SessionValidator;
} catch (e) {}
